import React from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { X, Plus } from 'lucide-react';

const SIZE_ORDER = ['S', 'M', 'L', 'XL', '2XL', 'XXL', '3XL', 'XXXL', '4XL', '5XL', '6XL', '7XL', '8XL', '9XL', 'Free', 'Free Size'];
const orderOf = (s) => { const i = SIZE_ORDER.indexOf(s); return i === -1 ? 999 : i; };

// Optional size-wise tentative pricing editor. Each group = a set of sizes + a
// price. Used as the per-size default rate for forwarding designs in a Sales
// Order (falls back to the flat Tentative WSP for sizes not in any group).
const TentativeWspGroups = ({ sizes = [], groups = [], onChange }) => {
  const sortedSizes = [...sizes].sort((a, b) => orderOf(a) - orderOf(b));

  const addGroup = () => onChange([...(groups || []), { sizes: [], price: '' }]);
  const removeGroup = (i) => onChange(groups.filter((_, idx) => idx !== i));
  const setPrice = (i, val) => onChange(groups.map((g, idx) => (idx === i ? { ...g, price: val } : g)));
  const toggleSize = (i, sz) => onChange(groups.map((g, idx) => {
    if (idx !== i) return g;
    const has = (g.sizes || []).includes(sz);
    return { ...g, sizes: has ? g.sizes.filter(s => s !== sz) : [...(g.sizes || []), sz] };
  }));

  return (
    <div className="space-y-2" data-testid="twg-editor">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">Size-wise pricing <span className="text-gray-400">(optional — overrides the flat rate above for chosen sizes)</span></span>
        <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={addGroup} data-testid="twg-add-group">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add price group
        </Button>
      </div>

      {(groups || []).length === 0 && (
        <p className="text-[11px] text-gray-400">No size-wise groups. e.g. M–XXL ₹750, 3XL–5XL ₹850.</p>
      )}

      {(groups || []).map((g, i) => (
        <div key={i} className="rounded-md border border-gray-200 p-2.5 space-y-2 bg-gray-50/60" data-testid={`twg-group-${i}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-1">
              <span className="text-[11px] font-semibold text-gray-500 uppercase">Price ₹</span>
              <Input type="number" min="0" step="0.01" value={g.price ?? ''} placeholder="e.g. 750"
                onChange={e => setPrice(i, e.target.value)} onWheel={e => e.target.blur()}
                className="h-8 w-32 text-sm" data-testid={`twg-price-${i}`} />
            </div>
            <button type="button" onClick={() => removeGroup(i)} className="text-red-400 hover:text-red-600 p-1" title="Remove group" data-testid={`twg-remove-${i}`}>
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {sortedSizes.length === 0 && <span className="text-[11px] text-gray-400">No sizes in master list — add sizes via Catalogue options.</span>}
            {sortedSizes.map(sz => {
              const active = (g.sizes || []).includes(sz);
              return (
                <button key={sz} type="button" onClick={() => toggleSize(i, sz)} data-testid={`twg-size-${sz}-${i}`}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${active ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-gray-600 border-gray-300 hover:border-sky-400'}`}>
                  {sz}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default TentativeWspGroups;
